import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api, onUnauthorized } from '../lib/api.js';
import { clearTokens, hasTokens, hydrateTokens, setTokens } from '../lib/storage.js';
import { registerPush } from '../lib/push.js';

const AuthContext = createContext(null);

/** Pages an expired or deleted session must not bounce to sign-in. */
const ALWAYS_PUBLIC = new Set(['/privacy', '/terms', '/delete-account', '/account-deleted', '/pay']);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Bootstrap: read persisted tokens, then confirm them against the API.
  //
  // Crucially, a *transient* failure (no network yet on a cold start, or the
  // server briefly 5xx-ing) must NOT clear the stored session — doing so would
  // force a fresh login on every launch whenever connectivity is a beat slow,
  // which on mobile is common. We only discard tokens on a genuine auth
  // rejection (the refresh token is invalid/expired). Network/server errors are
  // retried a few times, and if they still fail we keep the tokens so the
  // session recovers the moment the app can reach the API again.
  useEffect(() => {
    let cancelled = false;

    const isTransient = (err) => {
      const s = err?.status;
      // status 0 = couldn't reach the server; 5xx = server-side hiccup.
      return s === 0 || s === undefined || (typeof s === 'number' && s >= 500);
    };
    const sleep = (ms) =>
      new Promise((resolve) => {
        setTimeout(resolve, ms);
      });

    (async () => {
      await hydrateTokens();
      if (!hasTokens()) {
        if (!cancelled) setLoading(false);
        return;
      }

      // Up to 4 attempts with a short backoff, to ride out cold-start flakiness.
      const backoffs = [400, 900, 1800];
      for (let attempt = 0; ; attempt += 1) {
        try {
          const data = await api.get('/api/auth/me');
          if (!cancelled) setUser(data?.user ?? null);
          break;
        } catch (err) {
          if (isTransient(err) && attempt < backoffs.length) {
            await sleep(backoffs[attempt]);
            if (cancelled) return;
            continue;
          }
          // Genuine auth failure (invalid/expired refresh token, or transient
          // errors we've exhausted retries on). Only a true rejection should
          // wipe the session; a still-unreachable server keeps its tokens so a
          // later retry can recover without a re-login.
          if (!isTransient(err)) await clearTokens();
          if (!cancelled) setUser(null);
          break;
        }
      }
      if (!cancelled) setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Once signed in on the native app, ask for push permission and register the
  // device token. No-op on the web and after the first success.
  useEffect(() => {
    if (user) registerPush();
  }, [user]);

  // The fetch wrapper reports an unrecoverable 401 here rather than doing its
  // own navigation, so the redirect goes through the router.
  useEffect(
    () =>
      onUnauthorized(() => {
        setUser(null);
        queryClient.clear();
        // Pages anyone must be able to read signed out — the store-listing
        // links, and the confirmation after deleting an account (whose last
        // requests come back 401 by design). Sign out quietly and stay.
        if (ALWAYS_PUBLIC.has(window.location.pathname)) return;
        navigate('/login', { replace: true, state: { expired: true } });
      }),
    [navigate, queryClient],
  );

  const adopt = useCallback(
    async (data) => {
      await setTokens({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
      });
      setUser(data.user);
      return data.user;
    },
    [],
  );

  const login = useCallback(
    async (email, password) => {
      const data = await api.post('/api/auth/login', { email, password }, { auth: false });
      return adopt(data);
    },
    [adopt],
  );

  const register = useCallback(
    async ({ email, password, fullName, phone }) => {
      const data = await api.post(
        '/api/auth/register',
        { email, password, fullName, ...(phone ? { phone } : {}) },
        { auth: false },
      );
      return adopt(data);
    },
    [adopt],
  );

  // Mobile-OTP: ask the server to text a code to `phone`. Returns the server's
  // ack ({ ok, expiresInSeconds }); it never contains the code itself.
  const requestOtp = useCallback(
    (phone) => api.post('/api/auth/otp/request', { phone }, { auth: false }),
    [],
  );

  // Verify the code and adopt the resulting session. `fullName` is used only
  // when this is a first-time sign-up for the number.
  const verifyOtp = useCallback(
    async (phone, code, fullName, referralCode) => {
      const data = await api.post(
        '/api/auth/otp/verify',
        { phone, code, ...(fullName ? { fullName } : {}), ...(referralCode ? { referralCode } : {}) },
        { auth: false },
      );
      await adopt(data);
      return data; // caller may read data.isNewUser
    },
    [adopt],
  );

  // Save onboarding / profile fields (name, home-location pin) and refresh the
  // in-memory user so the app reflects them immediately.
  const updateProfile = useCallback(async (patch) => {
    const data = await api.patch('/api/me', patch);
    if (data?.user) setUser(data.user);
    return data?.user ?? null;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/api/auth/logout');
    } catch {
      // Revoking server-side is best-effort; the local session goes either way.
    }
    await clearTokens();
    setUser(null);
    queryClient.clear();
    navigate('/', { replace: true });
  }, [navigate, queryClient]);

  const value = useMemo(
    () => ({
      user,
      loading,
      login,
      register,
      requestOtp,
      verifyOtp,
      updateProfile,
      logout,
      // Adopt a session the server issued outside login/register — e.g. applying
      // as a delivery partner changes the account's role and returns new tokens.
      adoptSession: adopt,
      isAdmin: user?.role === 'admin' || user?.role === 'app_admin',
      isAuthenticated: Boolean(user),
      refreshUser: async () => {
        const data = await api.get('/api/auth/me');
        setUser(data?.user ?? null);
        return data?.user ?? null;
      },
    }),
    [user, loading, login, register, requestOtp, verifyOtp, updateProfile, logout, adopt],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside an <AuthProvider>');
  return ctx;
}

export default AuthContext;
